import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, MapPin, Lock, ShoppingBag, LogOut, Loader2, Truck } from 'lucide-react';
import { useUser } from '../context/UserContext';
import { updateUserProfile, changePassword, getUserOrders } from '../api/userAuth';
import SEO from '../components/SEO';
import { OrderStatusBar } from '../components/OrderStatusBar';

const ProfilePage: React.FC = () => {
  const { user, token, logout, login } = useUser();
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState<'details' | 'address' | 'security' | 'orders'>('details');
  const contentRef = useRef<HTMLDivElement>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  
  // Forms state
  const [detailsForm, setDetailsForm] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    phone: user?.phone || '',
  });
  
  const [addressForm, setAddressForm] = useState({
    street: user?.address?.street || '',
    city: user?.address?.city || '',
    state: user?.address?.state || '',
    zip: user?.address?.zip || '',
    country: user?.address?.country || 'India',
  });
  
  const [securityForm, setSecurityForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [orders, setOrders] = useState<any[]>([]);
  const [loadingOrders, setLoadingOrders] = useState(false);

  useEffect(() => {
    if (!user || !token) {
      navigate('/login');
    }
  }, [user, token, navigate]);

  useEffect(() => {
    if (activeTab === 'orders' && token) {
      const fetchOrders = async () => {
        setLoadingOrders(true);
        try {
          const data = await getUserOrders(token);
          setOrders(data.orders);
        } catch (error) {
          console.error('Failed to fetch orders:', error);
        } finally {
          setLoadingOrders(false);
        }
      };
      fetchOrders();
    }
  }, [activeTab, token]);

  const showMessage = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  };

  const selectTab = (tab: 'details' | 'address' | 'security' | 'orders') => {
    setActiveTab(tab);
    window.setTimeout(() => {
      contentRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 0);
  };

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;
    
    setIsSubmitting(true);
    try {
      const data = await updateUserProfile(token, { ...detailsForm, address: user?.address });
      login(token, data.user);
      showMessage('success', 'Profile updated successfully!');
    } catch (error: any) {
      showMessage('error', error.message || 'Failed to update profile');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateAddress = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;
    
    setIsSubmitting(true);
    try {
      const data = await updateUserProfile(token, {
        firstName: user?.firstName,
        lastName: user?.lastName,
        phone: user?.phone,
        address: addressForm
      });
      login(token, data.user);
      showMessage('success', 'Address updated successfully!');
    } catch (error: any) {
      showMessage('error', error.message || 'Failed to update address');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;
    
    if (securityForm.newPassword !== securityForm.confirmPassword) {
      return showMessage('error', 'New passwords do not match');
    }
    
    setIsSubmitting(true);
    try {
      await changePassword(token, {
        currentPassword: securityForm.currentPassword,
        newPassword: securityForm.newPassword
      });
      showMessage('success', 'Password changed successfully!');
      setSecurityForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error: any) {
      showMessage('error', error.message || 'Failed to change password');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <SEO 
        title="My Profile - c nine" 
        description="Manage your c nine account profile, address, and orders." 
      />
      
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">My Profile</h1>
            <p className="mt-2 text-sm text-gray-600">
              Welcome back, {user.firstName}! Manage your account settings and orders here.
            </p>
          </div>
          <div className="hidden md:flex">
             <button
                onClick={() => {
                  if (window.confirm('Do you want to log out?')) {
                    logout();
                    navigate('/');
                  }
                }}
                className="flex items-center space-x-2 text-red-600 hover:text-red-700 transition-colors bg-red-50 hover:bg-red-100 px-4 py-2 rounded-lg"
              >
                <LogOut className="w-5 h-5" />
                <span>Log Out</span>
              </button>
          </div>
        </div>

        <div className="flex flex-col md:flex-row gap-8">
          {/* Sidebar */}
          <div className="md:w-64 flex-shrink-0">
            <nav className="space-y-1">
              <button
                onClick={() => selectTab('details')}
                className={`w-full flex items-center space-x-3 px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                  activeTab === 'details'
                    ? 'bg-primary-50 text-primary-700'
                    : 'text-gray-900 hover:bg-gray-100'
                }`}
              >
                <User className="w-5 h-5" />
                <span>Personal Details</span>
              </button>
              
              <button
                onClick={() => selectTab('address')}
                className={`w-full flex items-center space-x-3 px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                  activeTab === 'address'
                    ? 'bg-primary-50 text-primary-700'
                    : 'text-gray-900 hover:bg-gray-100'
                }`}
              >
                <MapPin className="w-5 h-5" />
                <span>Shipping Address</span>
              </button>
              
              <button
                onClick={() => selectTab('security')}
                className={`w-full flex items-center space-x-3 px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                  activeTab === 'security'
                    ? 'bg-primary-50 text-primary-700'
                    : 'text-gray-900 hover:bg-gray-100'
                }`}
              >
                <Lock className="w-5 h-5" />
                <span>Security</span>
              </button>
              
              <button
                onClick={() => selectTab('orders')}
                className={`w-full flex items-center space-x-3 px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                  activeTab === 'orders'
                    ? 'bg-primary-50 text-primary-700'
                    : 'text-gray-900 hover:bg-gray-100'
                }`}
              >
                <ShoppingBag className="w-5 h-5" />
                <span>Order History</span>
              </button>
            </nav>
            
            <div className="mt-8 md:hidden">
              <button
                onClick={() => {
                  if (window.confirm('Do you want to log out?')) {
                    logout();
                    navigate('/');
                  }
                }}
                className="w-full flex items-center justify-center space-x-2 text-red-600 hover:text-red-700 transition-colors bg-red-50 hover:bg-red-100 px-4 py-3 rounded-lg"
              >
                <LogOut className="w-5 h-5" />
                <span>Log Out</span>
              </button>
            </div>
          </div>

          {/* Main Content */}
          <div ref={contentRef} className="flex-1 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden scroll-mt-4">
            <div className="p-6 sm:p-8">
              
              {message && (
                <div className={`mb-6 p-4 rounded-lg flex items-center justify-between ${
                  message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
                }`}>
                  <p>{message.text}</p>
                  <button onClick={() => setMessage(null)} className="text-gray-500 hover:text-gray-700">
                    &times;
                  </button>
                </div>
              )}

              {/* DETAILS TAB */}
              {activeTab === 'details' && (
                <div>
                  <h2 className="text-xl font-bold text-gray-900 mb-6">Personal Details</h2>
                  <form onSubmit={handleUpdateProfile} className="space-y-6 max-w-lg">
                    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                        <input
                          type="text"
                          required
                          value={detailsForm.firstName}
                          onChange={(e) => setDetailsForm({ ...detailsForm, firstName: e.target.value })}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                        <input
                          type="text"
                          required
                          value={detailsForm.lastName}
                          onChange={(e) => setDetailsForm({ ...detailsForm, lastName: e.target.value })}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email (Cannot be changed)</label>
                      <input
                        type="email"
                        disabled
                        value={user.email}
                        className="w-full px-4 py-2 border border-gray-200 bg-gray-50 rounded-lg text-gray-500 cursor-not-allowed"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                      <input
                        type="tel"
                        value={detailsForm.phone}
                        onChange={(e) => setDetailsForm({ ...detailsForm, phone: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                    
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="bg-primary-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-primary-700 transition-colors disabled:opacity-50"
                    >
                      {isSubmitting ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Save Changes'}
                    </button>
                  </form>
                </div>
              )}

              {/* ADDRESS TAB */}
              {activeTab === 'address' && (
                <div>
                  <h2 className="text-xl font-bold text-gray-900 mb-6">Shipping Address</h2>
                  <form onSubmit={handleUpdateAddress} className="space-y-6 max-w-lg">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Street Address</label>
                      <input
                        type="text"
                        required
                        value={addressForm.street}
                        onChange={(e) => setAddressForm({ ...addressForm, street: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                    
                    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                        <input
                          type="text"
                          required
                          value={addressForm.city}
                          onChange={(e) => setAddressForm({ ...addressForm, city: e.target.value })}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">State / Province</label>
                        <input
                          type="text"
                          required
                          value={addressForm.state}
                          onChange={(e) => setAddressForm({ ...addressForm, state: e.target.value })}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">ZIP / Postal Code</label>
                        <input
                          type="text"
                          required
                          value={addressForm.zip}
                          onChange={(e) => setAddressForm({ ...addressForm, zip: e.target.value })}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                        <input
                          type="text"
                          required
                          value={addressForm.country}
                          onChange={(e) => setAddressForm({ ...addressForm, country: e.target.value })}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                        />
                      </div>
                    </div>
                    
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="bg-primary-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-primary-700 transition-colors disabled:opacity-50"
                    >
                      {isSubmitting ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Save Address'}
                    </button>
                  </form>
                </div>
              )}

              {/* SECURITY TAB */}
              {activeTab === 'security' && (
                <div>
                  <h2 className="text-xl font-bold text-gray-900 mb-6">Security</h2>
                  <form onSubmit={handleChangePassword} className="space-y-6 max-w-lg">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Current Password</label>
                      <input
                        type="password"
                        required
                        value={securityForm.currentPassword}
                        onChange={(e) => setSecurityForm({ ...securityForm, currentPassword: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">New Password (Min 6 chars)</label>
                      <input
                        type="password"
                        required
                        minLength={6}
                        value={securityForm.newPassword}
                        onChange={(e) => setSecurityForm({ ...securityForm, newPassword: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
                      <input
                        type="password"
                        required
                        minLength={6}
                        value={securityForm.confirmPassword}
                        onChange={(e) => setSecurityForm({ ...securityForm, confirmPassword: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                    
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="bg-primary-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-primary-700 transition-colors disabled:opacity-50"
                    >
                      {isSubmitting ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Update Password'}
                    </button>
                  </form>
                </div>
              )}

              {/* ORDERS TAB */}
              {activeTab === 'orders' && (
                <div>
                  <h2 className="text-xl font-bold text-gray-900 mb-6">Order History</h2>
                  
                  {loadingOrders ? (
                    <div className="flex justify-center py-12">
                      <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
                    </div>
                  ) : orders.length === 0 ? (
                    <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
                      <ShoppingBag className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-900">No orders yet</h3>
                      <p className="mt-1 text-gray-500">When you make a purchase, it will appear here.</p>
                      <button
                        onClick={() => navigate('/products')}
                        className="mt-6 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                      >
                        Start Shopping
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {orders.map((order) => (
                        <div 
                          key={order._id} 
                          onClick={() => navigate(`/order/${order._id}`)}
                          className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm cursor-pointer hover:border-primary-500 transition-colors"
                        >
                          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                            <div>
                              <p className="text-xs text-gray-500 mb-1">ORDER PLACED</p>
                              <p className="text-sm font-medium text-gray-900">
                                {new Date(order.createdAt).toLocaleDateString('en-US', {
                                  year: 'numeric', month: 'long', day: 'numeric'
                                })}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 mb-1">TOTAL</p>
                              <p className="text-sm font-medium text-gray-900">
                                ₹{order.amount.toLocaleString('en-IN')}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 mb-1">STATUS</p>
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                order.status === 'paid' ? 'bg-green-100 text-green-800' : 
                                order.status === 'failed' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'
                              }`}>
                                {order.status.toUpperCase()}
                              </span>
                            </div>
                            <div className="sm:text-right">
                              <p className="text-xs text-gray-500 mb-1">ORDER ID</p>
                              <p className="text-sm text-gray-900 font-mono">{order.razorpayOrderId || order._id}</p>
                            </div>
                          </div>
                          <div className="bg-white border-b border-gray-100">
                            <OrderStatusBar 
                              status={order.status} 
                              trackingStatus={order.trackingStatus} 
                              createdAt={order.createdAt}
                              etd={order.etd}
                              awbCode={order.awbCode}
                              isCompact={true} 
                            />
                          </div>

                          {(order.trackingStatus || order.awbCode) && (
                            <div className="bg-blue-50 px-6 py-3 border-b border-gray-200 flex items-center justify-between">
                              <div className="flex items-center">
                                <Truck className="w-5 h-5 text-blue-600 mr-2" />
                                <div>
                                  {order.awbCode && (
                                    <p className="text-xs text-blue-700 mt-0.5 font-medium">
                                      Tracking AWB: {order.awbCode}
                                    </p>
                                  )}
                                </div>
                              </div>
                              {order.awbCode && (
                                <a 
                                  href={`https://shiprocket.co/tracking/${order.awbCode}`}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="text-sm font-medium text-blue-700 hover:text-blue-800 underline"
                                >
                                  Track Package
                                </a>
                              )}
                            </div>
                          )}
                          
                          <div className="p-6">
                            <h4 className="text-sm font-medium text-gray-900 mb-4">Items in this order:</h4>
                            <ul className="divide-y divide-gray-200">
                              {order.items.map((item: any, idx: number) => {
                                const variantImage = item.selectedColor && item.productId?.colorVariants?.find((v: any) => v.name === item.selectedColor)?.image;
                                const itemImg = variantImage || item.productId?.image;

                                return (
                                  <li key={idx} className="py-4 flex items-center justify-between">
                                    <div className="flex items-center space-x-4">
                                      <div className="flex-shrink-0 w-12 h-12 bg-gray-100 rounded-md overflow-hidden border border-gray-100">
                                        {itemImg ? (
                                          <img src={itemImg} alt={item.productId?.title} className="w-full h-full object-cover" />
                                        ) : (
                                          <div className="w-full h-full flex items-center justify-center text-gray-400">?</div>
                                        )}
                                      </div>
                                      <div>
                                        <p className="text-sm font-medium text-gray-900">{item.productId?.title || 'Unknown Product'}</p>
                                        {item.selectedColor && (
                                          <span className="inline-block text-[11px] font-semibold text-indigo-700 bg-indigo-50 border border-indigo-100 px-2 py-0.5 rounded mt-0.5">
                                            Color: {item.selectedColor}
                                          </span>
                                        )}
                                        <p className="text-sm text-gray-500 mt-0.5">Qty: {item.quantity}</p>
                                      </div>
                                    </div>
                                    <div className="text-sm font-medium text-gray-900">
                                      ₹{item.priceAtPurchase.toLocaleString('en-IN')}
                                    </div>
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
